/**
 * AgentParameterMenu - Dropdown menu for inserting or creating agent parameters
 *
 * Shows available agent parameters from the tool's agentParameterSchema.
 * Reads directly from nodeEditorStore draft state so params created on the
 * Tool tab are immediately visible here (fixes the reactivity bug).
 * Supports inline creation of new parameters without switching tabs.
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuGroup,
  MenuGroupHeader,
  MenuDivider,
  Button,
  Input,
  Textarea,
  Field,
  Select,
  Tooltip,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { BotRegular, AddRegular } from '@fluentui/react-icons';
import type { Token } from '@/types/valueSegment';
import { TokenType, TokenBrandColors } from '@/types/valueSegment';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';

const useStyles = makeStyles({
  menuItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalXXS,
  },
  menuItemTitle: {
    fontWeight: tokens.fontWeightMedium,
  },
  menuItemDescription: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  menuItemBadge: {
    width: '8px',
    height: '8px',
    borderRadius: tokens.borderRadiusCircular,
    flexShrink: 0,
  },
  botIcon: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: 1,
  },
  emptyState: {
    padding: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    textAlign: 'center',
  },
  createForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    minWidth: '240px',
  },
  createFormActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'flex-end',
  },
});

/**
 * Schema property type for agent parameters
 */
interface SchemaProperty {
  type?: string;
  description?: string;
  title?: string;
}

/**
 * Agent parameter schema type
 */
export interface AgentParameterSchema {
  type?: string;
  properties?: Record<string, SchemaProperty>;
  required?: string[];
}

/**
 * Generate tokens from an agentParameterSchema
 */
export function generateTokensFromAgentSchema(schema: AgentParameterSchema | undefined): Token[] {
  if (!schema || !schema.properties) {
    return [];
  }

  return Object.entries(schema.properties).map(([propName, propSchema]) => ({
    key: `agent_param_${propName}`,
    title: propSchema.title ?? propName,
    name: propName,
    tokenType: TokenType.AGENTPARAMETER,
    type: propSchema.type ?? 'string',
    brandColor: TokenBrandColors[TokenType.AGENTPARAMETER],
    value: `\${agentParameters('${propName}')}`,
    description: propSchema.description ?? `Agent parameter: ${propName}`,
  }));
}

const PARAM_TYPES = ['string', 'integer', 'number', 'boolean', 'array', 'object'] as const;
const PARAM_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;

export interface AgentParameterMenuProps {
  /** Called when a parameter is selected (inserts token into the field) */
  onSelect: (token: Token) => void;
  /** Whether the menu trigger is disabled */
  disabled?: boolean;
  /** Button size */
  size?: 'small' | 'medium' | 'large';
  /** Custom className for the trigger button (for chip-style rendering) */
  triggerClassName?: string;
  /**
   * Schema passed as prop (legacy). The component prefers draft state from the store
   * when available, falling back to this prop for backwards compatibility.
   */
  schema?: AgentParameterSchema | undefined;
  /** Field name for auto-naming new parameters (e.g., the swagger field key) */
  fieldName?: string;
  /** Field description from swagger — used as default description for new parameters */
  fieldDescription?: string;
  /** Field schema type from swagger (e.g., 'string', 'integer') — defaults the param type */
  fieldType?: string;
}

/**
 * AgentParameterMenu - Dropdown for selecting or creating agent parameters.
 *
 * Reads schema from nodeEditorStore draft state so Tool tab edits are
 * reflected immediately. Falls back to the schema prop.
 */
export const AgentParameterMenu = ({
  onSelect,
  disabled = false,
  size = 'small',
  schema: schemaProp,
  fieldName,
  fieldDescription: fieldDescriptionProp,
  fieldType: fieldTypeProp,
  triggerClassName,
}: AgentParameterMenuProps) => {
  const styles = useStyles();

  // Read schema from draft state first, fall back to prop.
  // This fixes the reactivity bug: Tool tab writes to parameterValues['_agentParameterSchema'],
  // and we read from the same source.
  const draftSchema = useNodeEditorStore(
    (s) => s.parameterValues['_agentParameterSchema']
  ) as AgentParameterSchema | null | undefined;
  const setParameter = useNodeEditorStore((s) => s.setParameter);

  const schema = useMemo((): AgentParameterSchema | undefined => {
    if (draftSchema !== undefined) return draftSchema ?? undefined;
    return schemaProp;
  }, [draftSchema, schemaProp]);

  const agentTokens = useMemo(() => generateTokensFromAgentSchema(schema), [schema]);
  const existingNames = useMemo(
    () => new Set(Object.keys(schema?.properties ?? {})),
    [schema],
  );
  const hasTokens = agentTokens.length > 0;

  // Menu open state (controlled so we can close programmatically on cancel/escape)
  const [menuOpen, setMenuOpen] = useState(false);

  // Inline creation state
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<string>('string');
  const [newDescription, setNewDescription] = useState('');

  // Focus the name input when the create form opens
  const nameInputCallbackRef = useCallback((el: HTMLInputElement | null) => {
    if (el && isCreating) el.focus();
  }, [isCreating]);

  const nameError = useMemo(() => {
    if (!newName) return undefined;
    if (existingNames.has(newName)) return 'Name already exists';
    if (!PARAM_NAME_PATTERN.test(newName)) return 'Start with a letter; use letters, numbers, underscores';
    return undefined;
  }, [newName, existingNames]);

  const handleStartCreate = useCallback(() => {
    // Sanitize field name for use as param name: take last segment if dotted,
    // replace invalid chars with underscore, ensure starts with letter
    const raw = fieldName ?? 'param';
    // Take last segment for nested keys like 'body.recipients'
    const lastSegment = raw.split('.').pop() ?? raw;
    // Replace invalid chars, ensure starts with letter
    let suggested = lastSegment.replace(/[^a-zA-Z0-9_]/g, '_').replace(/^[^a-zA-Z]+/, '');
    if (!suggested) suggested = 'param';
    // Ensure unique
    if (existingNames.has(suggested)) {
      let i = 1;
      while (existingNames.has(`${suggested}${i}`)) i++;
      suggested = `${suggested}${i}`;
    }
    setNewName(suggested);
    // Map swagger type to agent param type — default to 'string' if unknown
    const mappedType = PARAM_TYPES.includes(fieldTypeProp as typeof PARAM_TYPES[number])
      ? fieldTypeProp!
      : 'string';
    setNewType(mappedType);
    setNewDescription(fieldDescriptionProp ?? '');
    setIsCreating(true);
  }, [fieldName, fieldDescriptionProp, fieldTypeProp, existingNames]);

  const handleCreate = useCallback(() => {
    if (!newName || nameError) return;

    const currentSchema: AgentParameterSchema = schema ?? { type: 'object', properties: {} };
    const updatedSchema: AgentParameterSchema = {
      ...currentSchema,
      type: currentSchema.type ?? 'object',
      properties: {
        ...currentSchema.properties,
        [newName]: { type: newType, description: newDescription || `Value for ${newName}` },
      },
    };
    const hasProperties = Object.keys(updatedSchema.properties!).length > 0;
    setParameter('_agentParameterSchema', hasProperties ? updatedSchema : null);

    const token: Token = {
      key: `agent_param_${newName}`,
      title: newName,
      name: newName,
      tokenType: TokenType.AGENTPARAMETER,
      type: newType,
      brandColor: TokenBrandColors[TokenType.AGENTPARAMETER],
      value: `\${agentParameters('${newName}')}`,
      description: newDescription || `Agent parameter: ${newName}`,
    };
    onSelect(token);
    setMenuOpen(false);
    setIsCreating(false);
  }, [newName, newType, newDescription, nameError, schema, setParameter, onSelect]);

  const handleCancelCreate = useCallback(() => {
    setMenuOpen(false);
    setIsCreating(false);
    setNewName('');
  }, []);

  const handleOpenChange = useCallback((_e: unknown, data: { open: boolean }) => {
    setMenuOpen(data.open);
    if (!data.open) {
      setIsCreating(false);
      setNewName('');
    } else if (!hasTokens) {
      // No existing params — go straight to creation form
      handleStartCreate();
    }
  }, [hasTokens, handleStartCreate]);

  return (
    <Menu open={menuOpen} onOpenChange={handleOpenChange}>
      <MenuTrigger disableButtonEnhancement>
        <Tooltip
          content={hasTokens ? 'Agent parameters' : 'Create agent parameter'}
          relationship="label"
        >
          {triggerClassName ? (
            <button
              type="button"
              className={triggerClassName}
              disabled={disabled}
              aria-label={hasTokens ? 'Agent parameters' : 'Create agent parameter'}
            >
              <BotRegular aria-hidden="true" className={styles.botIcon} />
            </button>
          ) : (
            <Button
              appearance="subtle"
              size={size}
              icon={<BotRegular />}
              disabled={disabled}
              aria-label={hasTokens ? 'Agent parameters' : 'Create agent parameter'}
            />
          )}
        </Tooltip>
      </MenuTrigger>

      <MenuPopover>
        <MenuList>
          {!isCreating ? (
            <>
              <MenuGroup>
                <MenuGroupHeader>Agent Parameters</MenuGroupHeader>
                {hasTokens ? (
                  agentTokens.map((token) => (
                    <MenuItem
                      key={token.key}
                      onClick={() => onSelect(token)}
                      icon={
                        <div
                          className={styles.menuItemBadge}
                          style={{ backgroundColor: token.brandColor }}
                        />
                      }
                    >
                      <div className={styles.menuItem}>
                        <span className={styles.menuItemTitle}>{token.title}</span>
                        <span className={styles.menuItemDescription}>
                          {token.description}
                        </span>
                      </div>
                    </MenuItem>
                  ))
                ) : (
                  <Text className={styles.emptyState}>
                    No parameters defined yet
                  </Text>
                )}
              </MenuGroup>
              <MenuDivider />
              <MenuItem icon={<AddRegular />} onClick={handleStartCreate}>
                Create new parameter
              </MenuItem>
            </>
          ) : (
            <div className={styles.createForm}>
              <Field
                label="Name"
                validationState={nameError ? 'error' : 'none'}
                validationMessage={nameError ?? null}
                size="small"
              >
                <Input
                  ref={nameInputCallbackRef}
                  size="small"
                  value={newName}
                  onChange={(_, d) => setNewName(d.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !nameError && newName) handleCreate();
                    if (e.key === 'Escape') handleCancelCreate();
                    e.stopPropagation();
                  }}
                  placeholder="parameterName"
                />
              </Field>
              <Field label="Type" size="small">
                <Select
                  size="small"
                  value={newType}
                  onChange={(_, d) => setNewType(d.value)}
                >
                  {PARAM_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Description" size="small">
                <Textarea
                  size="small"
                  value={newDescription}
                  onChange={(_, d) => setNewDescription(d.value)}
                  placeholder="What this parameter is for"
                  resize="vertical"
                  rows={2}
                />
              </Field>
              <div className={styles.createFormActions}>
                <Button size="small" appearance="subtle" onClick={handleCancelCreate}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  appearance="primary"
                  disabled={!newName || !!nameError}
                  onClick={handleCreate}
                >
                  Create
                </Button>
              </div>
            </div>
          )}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

AgentParameterMenu.displayName = 'AgentParameterMenu';
